import { useMemo, useState } from 'react';
import { Folder, FileVideo, ChevronRight, Home } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useStorages, useStorageFiles } from '@/api/fastchannel';
import { buildSourceUrl, isMediaFile } from './shared';
import styles from './Fastchannel.module.css';

/** The storage object behind a picked file — surfaced so callers that need to
 *  register an asset (Content Library) have the raw `storage_id` + `object_key`,
 *  not just the built playable URL. Optional second arg keeps existing callers
 *  (schedule picker) working unchanged. */
export interface FileSelection {
  storageId: string;
  objectKey: string;
  /** Size from the storage listing, so a mapped asset shows its size without a
   *  server HeadObject. Undefined when the listing omitted it. */
  sizeBytes?: number;
}

interface FileManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  onSelect: (sourceUrl: string, selection: FileSelection) => void;
}

function basename(key: string, path: string): string {
  return key.replace(path, '').replace(/^\//, '').replace(/\/$/, '');
}

export function FileManagerDialog({ open, onOpenChange, serviceUuid, onSelect }: FileManagerDialogProps) {
  const { data: storages = [], isLoading: storagesLoading } = useStorages(serviceUuid);
  const [storageId, setStorageId] = useState<string>('');
  const [path, setPath] = useState('/');
  const [marker, setMarker] = useState('/');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const activeStorage = useMemo(
    () => storages.find((s) => s.id === storageId) ?? storages[0],
    [storages, storageId],
  );

  const { data, isLoading } = useStorageFiles(serviceUuid, activeStorage?.id, path, marker);
  const folders = data?.common_prefixes ?? [];
  const files = (data?.contents ?? []).filter((f) => isMediaFile(f.key));

  const segments = useMemo(() => path.split('/').filter(Boolean), [path]);

  const goTo = (depth: number) => {
    const next = depth < 0 ? '/' : `${segments.slice(0, depth + 1).join('/')}/`;
    setPath(next);
    setMarker('/');
    setSelectedKey(null);
  };

  const openFolder = (prefix: string) => {
    setPath(prefix);
    setMarker('/');
    setSelectedKey(null);
  };

  const confirm = () => {
    if (activeStorage && selectedKey) {
      onSelect(buildSourceUrl(activeStorage, selectedKey), {
        storageId: activeStorage.id,
        objectKey: selectedKey,
        sizeBytes: files.find((f) => f.key === selectedKey)?.size,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select media</DialogTitle>
          <DialogDescription>Pick a video file (.mp4 or .m3u8) to schedule.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {storagesLoading ? (
            <Skeleton style={{ height: 320 }} />
          ) : storages.length === 0 ? (
            <EmptyState
              icon={<Folder size={20} />}
              title="No storage attached"
              description="This service has no media storage configured yet."
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Select value={activeStorage?.id ?? ''} onValueChange={(v) => { setStorageId(v); setPath('/'); setMarker('/'); setSelectedKey(null); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose storage" />
                </SelectTrigger>
                <SelectContent>
                  {storages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.title || s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className={styles.fileBreadcrumb}>
                <button type="button" className={styles.crumbBtn} onClick={() => goTo(-1)} aria-label="Root">
                  <Home size={13} />
                </button>
                {segments.map((seg, i) => (
                  <span key={`${seg}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
                    <ChevronRight size={12} />
                    <button type="button" className={styles.crumbBtn} onClick={() => goTo(i)}>
                      {seg}
                    </button>
                  </span>
                ))}
              </div>

              {isLoading ? (
                <Skeleton style={{ height: 280 }} />
              ) : folders.length === 0 && files.length === 0 ? (
                <EmptyState icon={<Folder size={20} />} title="Empty folder" description="No media or sub-folders here." />
              ) : (
                <div className={styles.fileList}>
                  {folders.map((f) => (
                    <button key={f.prefix} type="button" className={styles.fileRow} onClick={() => openFolder(f.prefix)}>
                      <Folder size={16} className={styles.fileMuted} />
                      <span className={styles.fileName}>{basename(f.prefix, path)}</span>
                    </button>
                  ))}
                  {files.map((file) => (
                    <button
                      key={file.key}
                      type="button"
                      className={`${styles.fileRow}${selectedKey === file.key ? ` ${styles.fileRowActive}` : ''}`}
                      onClick={() => setSelectedKey(file.key)}
                    >
                      <FileVideo size={16} />
                      <span className={styles.fileName}>{basename(file.key, path)}</span>
                    </button>
                  ))}
                </div>
              )}

              {data?.is_truncated && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!data?.next_marker}
                    onClick={() => data?.next_marker && setMarker(data.next_marker)}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selectedKey} onClick={confirm}>
            Select file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
